// src/stores/authStore.js
import { create } from 'zustand';
import {
  getUserData as getStoredUserData,
  setUserData as setStoredUserData,
  clearUserData as clearStoredUserData,
  fetchCurrentUser,
} from '@/lib/authUtils';

// This is a non-React store, so it can't use the useAuth hook directly
// It uses the non-hook versions from authUtils.js
// Components should prefer using the useAuth hook when possible

// Create auth store
const useAuthStore = create((set, get) => ({
  // State
  user: getStoredUserData(),
  isHandlingTokenExpiration: false, // Race condition protection flag

  // Computed properties (selectors)
  // In Zustand, we can use getters or create custom hooks for computed properties
  isLoggedIn: () => !!get().user,

  // Actions
  setUser: (userData) => {
    setStoredUserData(userData);
    set({ user: userData });
  },

  clearUser: () => {
    clearStoredUserData();
    set({ user: null });
  },

  // Handle token expiration - clears user state and localStorage
  handleTokenExpiration: () => {
    const state = get();

    // Race condition protection: prevent multiple simultaneous token expiration handling
    if (state.isHandlingTokenExpiration) {
      console.log('DEBUG: Token expiration already being handled, skipping');
      return;
    }

    console.log('DEBUG: Token expired, clearing user authentication state');

    // Set flag to prevent race conditions
    set({ isHandlingTokenExpiration: true });

    clearStoredUserData();
    set({ user: null });

    // Check if current path is a protected route and redirect to login
    const currentPath = window.location.pathname;
    const protectedRoutes = [
      '/user-profile',
      '/create-expert-profile',
      '/edit-expert-profile',
      '/host/sessions',
      '/my-sessions',
      '/admin',
      '/session/',
    ];

    const isOnProtectedRoute = protectedRoutes.some((route) =>
      currentPath.startsWith(route)
    );

    if (isOnProtectedRoute) {
      console.log('DEBUG: Redirecting from protected route to signin');
      window.location.href = '/signin';
    }

    // Reset the flag after a short timeout to allow future token expiration handling
    setTimeout(() => {
      set({ isHandlingTokenExpiration: false });
    }, 1000);
  },

  // Refresh user data from the API
  refreshUser: async () => {
    console.log('DEBUG: authStore.refreshUser called');
    const userData = await fetchCurrentUser();
    console.log('DEBUG: fetchCurrentUser returned:', userData);
    if (userData) {
      console.log('DEBUG: Setting user data in store');
      set({ user: userData });
    }
    return userData;
  },
}));

// Export the store
export default useAuthStore;
